import React, { useEffect } from 'react';
import {
    Alert, Card, Container, Button,
} from "react-bootstrap";
import LABELS from 'app/shared/constants/Literals';
import { useDispatch } from 'react-redux';
import ARIA_LABELS from 'constants/ariaLabels';
import { toggleCartDrawer } from 'app/store/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import PickupScheduleButton from 'app/shared/components/PickupScheduleButton';
import { Box, Stack, StackProps} from "@mui/material";
import getBrandName from 'app/shared/utils/BrandHelper';
import { useAppSelector } from 'store/hooks';
import Constants, {
    ShellCustomEvent,
    ShellHeaderTitle,
    BALANCE_MESSAGE,
    HELLO_MESSAGE,
    POINTS_MESSAGE,
} from 'app/shared/constants/constants';
import {
 deleteLineItem, updateQuantity, setCartItemSelectedForEdit,
} from 'store/slices/tOrderSlice';
import { getShellPopToHomeResponseEvent } from 'app/shared/utils/ShellHelper';
import { OrderItem } from 'store/slices/tOrderTypes';
import { Place} from "@mui/icons-material";
import { publishShellEvent } from 'utilities/publishBridgeEvent';
import useShellBackNavigation from 'hooks/useShellBackNavigation';
import useCustomEvent from 'hooks/useCustomEvent';
import useMobileTopBar from 'hooks/useMobileTopBar';
import CartItem from "features/order/cartItem";
import BrandCategoryURL from 'app/features/Order/Cart/CateringLink';
import { BrowserView } from "react-device-detect";
import T_ROUTES from 'routes/routePaths';
import RewardsHeaderSection from 'features/rewards/RewardsHeaderSection';
import './cart.scss';
import CartError from './CartError';

const { brandTitle } = getBrandName();

type CartProps = {
    onCartCheckout: () => void;
    onPickupInfoChange: () => void;
    errorCode: string | null;
    onErrorClose: () => void;
    isShellApp?: boolean;
    isAnyApiLoading?: boolean;
};

function Cart({
    onCartCheckout,
    onPickupInfoChange,
    errorCode,
    onErrorClose,
    isShellApp,
    isAnyApiLoading,
}:CartProps) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {user, isGuest, cartOpen} = useAppSelector((state) => state.auth);
    useEffect(() => {
        if (!isShellApp || !cartOpen) return undefined;

        document.documentElement.classList.add('shell-cart-open');
        return () => {
            document.documentElement.classList.remove('shell-cart-open');
        };
    }, [isShellApp, cartOpen]);

    const {
        balanceAmount,
        balancePoints,
        firstName,
    } = useAppSelector((state) => state.userInfo);
    const {
        storeDescription,
        isStoreClosed,
        isStoreEligible,
        pickUpType,
    } = useAppSelector((state) => state.stores);
    const {
        orderLines: cartItems,
        total,
        wallets,
    } = useAppSelector((state) => state.tOrders);
    const hasRewaredSelected = useAppSelector((state) => state.rewards.hasRewaredSelected);
    const isStoreOpen = !(isStoreClosed || !isStoreEligible);
    const isCheckoutDisabled = pickUpType === "" || pickUpType === Constants.NO_PICKUP_TYPE || !isStoreOpen || Number(total) <= 0;
    const checkoutButtonDisableClass = isCheckoutDisabled ? 'disable-button' : '';
    const isCartEmpty = cartItems.length === 0;
    const onAddItems = () => {
        dispatch(toggleCartDrawer(false));
        navigate("/category");
    };
    const closeCartHandler = () => {
        dispatch(toggleCartDrawer(false));
    };

    function onNavigateBack() {
        closeCartHandler();
    }
    useCustomEvent(ShellCustomEvent.WEB_BUTTON_TAP, (eventName:string | null) => {
        if (eventName === ShellCustomEvent.POP_TO_HOME) {
            publishShellEvent(getShellPopToHomeResponseEvent(!!isAnyApiLoading));
        }
    });
    useShellBackNavigation({
        onBack: onNavigateBack,
        isBlocked: isAnyApiLoading,
        isSkipped: !cartOpen,
    });
    useMobileTopBar({
        title: ShellHeaderTitle.MY_CART,
        showBackButton: true,
        rightBarButton: {
            btnAction: ShellCustomEvent.POP_TO_HOME,
            btnTitle: ShellHeaderTitle.CANCEL,
        },
        isSkipUpdate: !cartOpen,
    });
    const buttonWidthClass = isShellApp ? "w-auto" : "w-75";
    let actionContainerProps: StackProps = {
        className: "mb-4 w-100",
        gap: 0,
        direction: "column",
        sx: {
            alignItems: "center",
            justifyContent: "center",
        },
    };
    if (isShellApp) {
        actionContainerProps = {
            className: "w-100 mb-4 px-3",
            gap: 2,
            direction: "row",
            sx: {
                alignItems: "center",
                justifyContent: "flex-end",
            },
        };
    }

    if (errorCode) {
       return (
            <CartError errorCode={errorCode} onErrorClose={onErrorClose} />
        );
    }

    return (
        <Card className={`border-0 h-100 ${isShellApp ? 'shell-cart d-flex flex-column bg-white' : ''}`}>
            <Card.Header className={`py-4 px-3 horizontalAlignCenter justify-content-between rounded-0 header-bg ${
                    isShellApp ? 'shell-cart-location' : ''
                }`}
            >
                <div className="horizontalAlignCenter">
                    {
                        !isShellApp && (<Place fontSize="large" className="map-location-icon"/>)
                    }
                    <div className="moreHeaderTextContent">
                        <span className="user-info">{brandTitle}</span>
                        <span>
                            {
                                isShellApp ? (
                                    <strong className="user-info">{storeDescription}</strong>
                                ) : (
                                    <Button
                                    disabled={storeDescription === "" || storeDescription === Constants.NO_LOCATION_SELECTED}
                                    variant="link"
                                    className="storeLink"
                                    onClick={() => {
                                        dispatch(toggleCartDrawer(false));
                                        navigate("/");
                                    }}
                                    >
                                        <strong className="user-info">{storeDescription}</strong>
                                    </Button>
                                )
                            }
                        </span>
                        <span>
                            <PickupScheduleButton
                                className="button-hover ml-0 m-0 border-0 p-0 pl-0 font-sub bg-transparent"
                                onClick={() => { onPickupInfoChange(); }}
                            />
                        </span>
                    </div>
                </div>
                {user && !isGuest && (
                                    <BrowserView>
                                        <div>
                                            <div className="ms-0 p-0">
                                                <p className="ms-1 user-greeting">
                                                    <b>
                                                        {HELLO_MESSAGE(firstName)}
                                                    </b>
                                                </p>
                                                <p
                                                    color="primary-light"
                                                    className="m-0 ms-1 font-sub"
                                                >
                                                    {POINTS_MESSAGE(balancePoints.toString()) }
                                                </p>
                                                <p className="m-0 ms-1 font-sub">
                                                    {BALANCE_MESSAGE(balanceAmount?.toFixed(2))}
                                                </p>
                                            </div>
                                        </div>
                                    </BrowserView>
                                )}
            </Card.Header>
            <Card.Body className={`overflow-auto ${isShellApp ? 'shell-cart-body' : ''}`}>
                <h5 color="primary" className="fontHeaderAndButton text-center mb-3">
                    {LABELS.CART.modalTitle}
                </h5>
                <BrandCategoryURL/>
                {
                    hasRewaredSelected && (
                    <RewardsHeaderSection
                        wallets={wallets || null}
                        isCartPage
                    />
                )
                }
                {isCartEmpty && <p className="font-sub">{LABELS.CART.noItems}</p>}
                {
                    cartItems.map((cartItem) => (
                        <CartItem
                            key={cartItem.itemUniqueId || cartItem.itemId}
                            cartItem={cartItem}
                            addToCart={(item:OrderItem) => {
                                dispatch(updateQuantity(
                                    {
                                        itemUniqueId: item.itemUniqueId || "",
                                        quantity: item.quantity + 1,
                                    },
                            ));
                            }}
                            removeFromCart={(item:OrderItem) => {
                                if (item.quantity - 1 <= 0) {
                                    dispatch(deleteLineItem(item));
                                } else {
                                    dispatch(
                                        updateQuantity(
                                        {
                                            itemUniqueId: item.itemUniqueId || "",
                                            quantity: item.quantity - 1,
                                        },
                                        ),
                                    );
                                }
                            }}
                            removeGroupFromCart={(item:OrderItem) => {
                                dispatch(deleteLineItem(item));
                            }}
                            editItemFromCart={(item:OrderItem) => {
                                dispatch(toggleCartDrawer(false));
                                dispatch(setCartItemSelectedForEdit(item));
                                navigate(T_ROUTES.MENU);
                            }}
                        />
                    ))
                }
            </Card.Body>
            <Card.Footer className={`d-flex verticalAlignCenter justify-content-center border-0 p-0 m-0 ${
                    isShellApp ? 'shell-cart-footer bg-white align-items-center' : ''
                }`}
            >
                    <Container className="bg-white">
                        <Box
                            className="d-flex flex-row justify-content-between toastCartTotal subtotal-font"
                        >
                        <h4 className="p-3 m-0">{LABELS.CART.subtotal}</h4>
                        <h4 className="p-3 m-0">
                            <span aria-label={ARIA_LABELS.CART.subtotal}>
                                $
                                {total || "0.00"}
                            </span>
                        </h4>
                        </Box>
                        <Stack
                            {...actionContainerProps}
                        >
                            <Button
                                className={`secondary-button ${buttonWidthClass}`}
                                variant="secondary"
                                onClick={onAddItems}
                                aria-label={ARIA_LABELS.CART.addItems}
                            >
                                {LABELS.CART.addItems}
                            </Button>
                            <Button
                                className={`order-now-card-button px-3 ${checkoutButtonDisableClass} ${buttonWidthClass}`}
                                variant="primary"
                                onClick={onCartCheckout}
                                aria-label={ARIA_LABELS.CART.checkout}
                                disabled={isCheckoutDisabled}
                            >
                                {LABELS.CART.checkout}
                            </Button>
                        </Stack>
                        {!isStoreOpen && cartItems?.length > 0
                        && (
                            <Alert variant="danger" className="mt-2 p-0 px-3 m-0 border-0 mt-2 font-sub error-main">
                                {LABELS.CART.storeIsNotAcceptingOrders}
                            </Alert>
                        )}
                    </Container>
            </Card.Footer>
        </Card>
    );
}

export default Cart;
